import React, { useEffect, useState, useRef, useMemo } from "react"
import { useParams } from 'react-router-dom'
import useVisualDesigns from "@eventdex/builder/src/hooks/useVisualDesigns"
import { useDispatch, useSelector } from "react-redux"
import { AppTypes, useDesignRoutes } from "@components/Canvas/hooks/useDesignRoutes"
import { changeActivePanel } from "@/store/reducers/init/actions"
import { changeIsAttendeeFloorMap, changeAttendees } from "@store/reducers/paper/actions"
import { getAttendees as getAttendeesSelector } from "@store/reducers/paper/selectors"
import * as L from 'leaflet'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import { getInitials } from "./utils/helpers"

// Import our refactored components and hooks
import { ToggleButton, DrawerContainer, DrawerBackdrop } from './components/StyledComponents';
import { DrawerHeader } from './components/DrawerHeader';
import { SearchFilters } from './components/SearchFilters';
import { TableList } from './components/TableList';
import { TableDetails } from './components/TableDetails';
import { useSyncAttendees } from './hooks/useSyncAttendees';
import { useSnackbar } from './hooks/useSnackbar';
import { zIndexLevels } from './utils/helpers';
import { mergeFabricDataWithAPIData } from './services/fabricDataService';
import { attachLiveSeating } from './services/liveSeating';
import { attachLiveDesign } from './services/liveDesign';
import { seatingForDesign } from '@components/Gala/seatAssignment';
import { compareTableLabels } from '@components/Gala/seatingSidebar/sidebarModel';
import { TableData, StatusColors, FabricObject } from './types';
import webSocket from '../../services/WebSocket';
console.log('WebSocket service loaded:', webSocket);

const AttendeeGalaLeafRefactored = () => {
  // Ensure WebSocket module is loaded
  const { id, eventId } = useParams<{ id: string, eventId: string }>()
  const dispatch = useDispatch()
  const { switchApp } = useDesignRoutes()
  
  // Get attendees from Redux store
  const attendeesFromStore = useSelector(getAttendeesSelector)
  
  // Get tableName from URL search params for table-specific view
  const urlParams = new URLSearchParams(window.location.search)
  const tableNameFromUrl = urlParams.get('tableName')
  const isTableSpecificView = !!tableNameFromUrl
  
  // State management
  const [isLoading, setIsLoading] = useState(true)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [allTables, setAllTables] = useState<TableData[]>([])
  const [filteredTables, setFilteredTables] = useState<TableData[]>([])
  const [selectedTable, setSelectedTable] = useState<TableData | null>(null)
  const [currentFilter, setCurrentFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [attendeeSearchTerm, setAttendeeSearchTerm] = useState('')
  const [ticketNameFilter, setTicketNameFilter] = useState('all')
  const [badgeName, setBadgeName] = useState('')
  const [statusColors, setStatusColors] = useState<StatusColors>({
    checkedin: '#008000',
    checkedout: '#f44336',
    none: '#808080'
  })
  
  // Refs
  const mapRef = useRef<L.Map | null>(null)
  const textLayersRef = useRef<any[]>([])
  const mapContainerRef = useRef<HTMLDivElement>(null)

  // Custom hooks
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const oId = process.env.REACT_APP_SF_ORG_ID || '00DF0000000BpyHMAS'

  // Fetch floor map design meta
  const storedDesignMeta = useVisualDesigns({
    category: 11,
    event_id: [eventId]
  })

  // The room as last saved, when a save arrived after the list was fetched.
  const [savedDesign, setSavedDesign] = useState<any>(null)
  useEffect(() => {
    setSavedDesign(null)
    if (!id) return
    return attachLiveDesign({ designId: id, onDesign: setSavedDesign })
  }, [id])

  const floorMapDesignMeta = useMemo(() => {
    if (!savedDesign) return storedDesignMeta
    return {
      ...storedDesignMeta,
      filteredVisualDesigns: storedDesignMeta.filteredVisualDesigns.map((design: any) =>
        design.id == savedDesign.id ? { ...design, ...savedDesign } : design
      )
    }
  }, [storedDesignMeta, savedDesign])
  const currentDesign = floorMapDesignMeta?.filteredVisualDesigns?.find((item: any) => item.id == id)


  
  // Map management functions
  const clearMapLayers = () => {
    if (!mapRef.current) return;
    
    console.log('Clearing existing map layers...');
    mapRef.current.eachLayer((layer: any) => {
      if (!layer._url) {
        mapRef.current?.removeLayer(layer);
      }
    });
    
    textLayersRef.current = [];
    console.log('Map layers cleared.');
  };

  const processFabricData = (map: L.Map, fabricJsJsonData: any) => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    const tables: TableData[] = []
    const textLayers: any[] = []

    // Get canvas dimensions for Y-coordinate flipping
    const canvasHeight = fabricJsJsonData.height || 600

    const processFabricObject = (
      fabricObject: FabricObject,
      parentOffsetX = 0,
      parentOffsetY = 0,
      parentScaleX = 1,
      parentScaleY = 1
    ) => {
      const {
        type, left, top, width = 0, height = 0, scaleX = 1, scaleY = 1,
        objects, text, src, radius = 0, fill, stroke, strokeWidth,
        originX = 'left', originY = 'top', angle = 0,
        isTable, isChair, attendeeData, tableData, fontSize, fontFamily, fontWeight
      } = fabricObject


      // Calculate positions and dimensions with Y-coordinate flipping
      const absCenterX = parentOffsetX + (left * parentScaleX)
      // Flip Y coordinate: subtract from canvas height
      const absCenterY = canvasHeight - (parentOffsetY + (top * parentScaleY))
      const effectiveWidth = width * scaleX * parentScaleX
      const effectiveHeight = height * scaleY * parentScaleY
      const effectiveRadius = radius * Math.max(scaleX * parentScaleX, scaleY * parentScaleY)

      // Calculate bounds
      let topLeftX, topLeftY
      if (originX === 'center') {
        topLeftX = absCenterX - (effectiveWidth / 2)
      } else {
        topLeftX = absCenterX
      }
      if (originY === 'center') {
        topLeftY = absCenterY - (effectiveHeight / 2)
      } else {
        topLeftY = absCenterY
      }

      // Compute visual center regardless of origin for marker placement
      const objectCenterX = topLeftX + (effectiveWidth / 2)
      const objectCenterY = topLeftY + (effectiveHeight / 2)

      // Update map bounds
      minX = Math.min(minX, topLeftX)
      minY = Math.min(minY, topLeftY)
      maxX = Math.max(maxX, topLeftX + effectiveWidth)
      maxY = Math.max(maxY, topLeftY + effectiveHeight)

      // Handle table groups
      if (type === 'group' && objects) {
        if (isTable && tableData && (tableData.tableName || tableData.tableNumber)) {
          // Use tableName if available, otherwise use tableNumber
          const displayTableName = tableData.tableName || tableData.tableNumber;
          
          const tableInfo = {
            ...tableData,
            tableName: displayTableName, // Ensure tableName is set for consistency
            capacity: tableData.attendees.length + (tableData.emptySeats || 0)
          }
          tables.push(tableInfo)

          // Skip creating table name marker if there's a text object in the group that already contains the table name
          // This prevents duplicate rendering of table names
          let hasTableNameText = false;
          if (objects) {
            for (const obj of objects) {
              if (obj.type === 'text' && obj.text === displayTableName) {
                hasTableNameText = true;
                break;
              }
            }
          }

          // Create table name marker - centered
          const tableNameLatLng = L.latLng(absCenterY, absCenterX)
          const baseFontSize = fontSize || 14
          const currentZoom = map.getZoom()
          const zoomScale = Math.pow(1.2, currentZoom)
          const initialScaledFontSize = baseFontSize * zoomScale
          
          let marker;
          
          if (!hasTableNameText) {
            // Only create a new marker if we don't already have text for this table
            const divIcon = L.divIcon({
              className: 'custom-text-icon',
              html: `<div style="
                display: flex;
                align-items: center;
                justify-content: center;
                width: 100%;
                height: 100%;
                text-align: center;
                font-size: ${initialScaledFontSize}px;
                font-weight: 600;
                color: ${tableData?.colors?.table || '#334155'};
                pointer-events: auto;
                text-shadow: 1px 1px 2px rgba(255,255,255,0.8);
                cursor: pointer;
              ">${displayTableName}</div>`,
              iconSize: [effectiveWidth, effectiveHeight],
              iconAnchor: [effectiveWidth/2, effectiveHeight/2]
            })
  
            marker = L.marker(tableNameLatLng, { 
              icon: divIcon,
              zIndexOffset: zIndexLevels.tables
            }).addTo(map)
            
            textLayers.push({
              layer: marker,
              baseFontSize: baseFontSize,
              className: 'table-name',
              originalHtml: displayTableName,
              tableData: tableInfo
            })
            
            // Add click handler to show drawer with specific table
            marker.on('click', () => {
              console.log('Table clicked:', tableInfo);
              setSelectedTable(tableInfo)
              setIsDrawerOpen(true)
            })
          }
        }

        // Process child objects - pass flipped Y coordinate
        objects.forEach(obj => {
          // Handle text elements within the table group
          if (isTable && obj.type === 'text' && obj.text) {
            // Check if this text is the table name
            const tableNameToCheck = tableData.tableName || tableData.tableNumber;
            const isTableNameText = obj.text === tableNameToCheck;
            
            // Calculate position for the text
            const textLatLng = L.latLng(
              canvasHeight - (parentOffsetY + (top * parentScaleY) + (obj.top * scaleY * parentScaleY)),
              parentOffsetX + (left * parentScaleX) + (obj.left * scaleX * parentScaleX)
            )
            
            const baseFontSize = obj.fontSize || 16
            const currentZoom = map.getZoom()
            const zoomScale = Math.pow(1.2, currentZoom)
            const initialScaledFontSize = baseFontSize * zoomScale * (obj.scaleX || 1)
            
            // For table name text, use the table style
            const textColor = isTableNameText 
              ? (tableData?.colors?.text || '#334155') 
              : (obj.fill || '#000000');
            
            const fontWeight = isTableNameText ? '600' : (obj.fontWeight || 'normal');
            
            const divIcon = L.divIcon({
              className: 'custom-text-icon',
              html: `<div style="
                display: flex;
                align-items: center;
                justify-content: center;
                width: 100%;
                height: 100%;
                text-align: ${obj.textAlign || 'left'};
                font-size: ${initialScaledFontSize}px;
                font-weight: ${fontWeight};
                color: ${textColor};
                font-family: ${obj.fontFamily || 'Arial'};
                white-space: nowrap;
                pointer-events: auto;
                cursor: pointer;
                ${isTableNameText ? 'text-shadow: 1px 1px 2px rgba(255,255,255,0.8);' : ''}
              ">${obj.text}</div>`,
              iconSize: [
                (obj.width * (obj.scaleX || 1) * scaleX * parentScaleX) + 40, // Add 40px padding for easier clicking
                (obj.height * (obj.scaleY || 1) * scaleY * parentScaleY) + 20  // Add 20px padding for easier clicking
              ],
              iconAnchor: [
                (obj.width * (obj.scaleX || 1) * scaleX * parentScaleX + 40) / 2, 
                (obj.height * (obj.scaleY || 1) * scaleY * parentScaleY + 20) / 2
              ]
            })
            
            const textMarker = L.marker(textLatLng, { 
              icon: divIcon,
              zIndexOffset: isTableNameText ? zIndexLevels.tables : zIndexLevels.text
            }).addTo(map)
            
            textLayers.push({
              layer: textMarker,
              baseFontSize: baseFontSize,
              className: isTableNameText ? 'table-name' : 'table-text',
              originalHtml: obj.text,
              fontWeight: obj.fontWeight,
              textAlign: obj.textAlign,
              fill: obj.fill,
              fontFamily: obj.fontFamily,
              tableData: tableData
            })
            
            // If this is a table name text, add click handler to show drawer with specific table
            if (isTableNameText) {
              // Create table info object for the click handler
              const clickTableInfo = {
                ...tableData,
                tableName: tableNameToCheck,
                capacity: tableData.attendees.length + (tableData.emptySeats || 0)
              };
              
              textMarker.on('click', () => {
                console.log('Table clicked:', clickTableInfo);
                setSelectedTable(clickTableInfo)
                setIsDrawerOpen(true)
              })
            }
            
            return
          }

          if (isChair && attendeeData) {
            obj.attendeeData = attendeeData
          }

          // Only process non-text child objects recursively to avoid duplicate rendering
          if (obj.type !== 'text') {
            processFabricObject(obj, absCenterX, parentOffsetY + (top * parentScaleY), scaleX * parentScaleX, scaleY * parentScaleY)
          }
        })
        return
      }

      // Handle individual objects
      let leafletLayer = null
      switch (type) {
        case 'rect':
          const rectBounds = L.latLngBounds(
            [topLeftY, topLeftX],
            [topLeftY + effectiveHeight, topLeftX + effectiveWidth]
          )
          
          // Check if this is a chair and apply status colors
          let rectStrokeColor = stroke || '#000000'
          let rectStrokeWidth = strokeWidth || 1
          let rectFillColor = fill || '#cccccc'
          let rectFillOpacity = 0.8
          
          if (isChair && attendeeData) {
            // Use status colors for chair borders
            rectStrokeColor = statusColors.none // Default for null/undefined
            rectStrokeWidth = 2
            rectFillColor = tableData?.colors?.chair || fill || 'transparent'
            rectFillOpacity = 0.3
            
            if (attendeeData?.assignedTicket?.checkedIn === true) {
              rectStrokeColor = statusColors.checkedin
              rectStrokeWidth = 3
              rectFillOpacity = 0.2
            } else if (attendeeData?.assignedTicket?.checkedIn === false) {
              rectStrokeColor = statusColors.checkedout
              rectStrokeWidth = 3
              rectFillOpacity = 0.2
            }
          }
          
          leafletLayer = L.rectangle(rectBounds, {
            color: rectStrokeColor,
            weight: rectStrokeWidth,
            fillColor: rectFillColor,
            fillOpacity: rectFillOpacity,
            pane: 'overlayPane',
            zIndexOffset: zIndexLevels.shapes
          }).addTo(map)
          
          // Add attendee initials if available for rectangular chairs
          if (isChair && attendeeData && attendeeData.name) {
            const initials = getInitials(attendeeData.name)
            const textLatLng = L.latLng(absCenterY, absCenterX)
            const baseFontSize = Math.max(Math.min(effectiveWidth, effectiveHeight) * 0.3, 10)
            const currentZoom = map.getZoom()
            const zoomScale = Math.pow(1.2, currentZoom)
            const initialScaledFontSize = baseFontSize * zoomScale

            // Use table-specific text color for attendee initials
            const textColor = tableData?.colors?.text || '#000000'

            // Make the circle size proportional to the chair size
            const circleSize = Math.max(Math.min(effectiveWidth, effectiveHeight) * 0.8, 24)

            const divIcon = L.divIcon({
              className: 'leaflet-div-icon',
              html: `<div style="
                width: ${circleSize}px;
                height: ${circleSize}px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: ${initialScaledFontSize}px;
                font-weight: 600;
                color: ${textColor};
                cursor: pointer;
                transition: all 0.2s ease;
                pointer-events: auto;
              ">${initials}</div>`,
              iconSize: [circleSize, circleSize],
              iconAnchor: [circleSize / 2, circleSize / 2]
            })
            
            const initialsMarker = L.marker(textLatLng, { 
              icon: divIcon,
              zIndexOffset: zIndexLevels.attendees
            }).addTo(map)

            // Add tooltip to both the chair shape and initials marker
            const tooltipContent = `
              <div style="font-weight: 600; margin-bottom: 4px;">${attendeeData.name}</div>
              <div style="color: #4b5563; margin-bottom: 2px;">${attendeeData.company || ''}</div>
              <div style="margin-top: 8px; font-size: 13px;">
                <div>Ticket: ${attendeeData.assignedTicket?.name || 'N/A'}</div>
                <div>Status: ${attendeeData.assignedTicket?.checkedIn ? 'Checked In' : 'Not Checked In'}</div>
                ${attendeeData.mealType ? `<div>Meal: ${attendeeData.mealType}</div>` : ''}
              </div>
            `
            
            // Bind tooltip to both the chair shape and initials marker
            leafletLayer.bindTooltip(tooltipContent, {
              permanent: false,
              direction: 'top',
              className: 'attendee-tooltip',
              offset: [0, -10],
              opacity: 1
            })

            initialsMarker.bindTooltip(tooltipContent, {
              permanent: false,
              direction: 'top',
              className: 'attendee-tooltip',
              offset: [0, -10],
              opacity: 1
            })

            // Add hover effects to both the chair shape and initials marker
            leafletLayer.on('mouseover', function() {
              // Close any open tooltips first
              map.eachLayer((layer: any) => {
                if (layer.isTooltipOpen && layer.isTooltipOpen()) {
                  layer.closeTooltip();
                }
              });
              leafletLayer.openTooltip();
            })

            leafletLayer.on('mouseout', function() {
              leafletLayer.closeTooltip();
            })

            initialsMarker.on('mouseover', function() {
              // Close any open tooltips first
              map.eachLayer((layer: any) => {
                if (layer.isTooltipOpen && layer.isTooltipOpen()) {
                  layer.closeTooltip();
                }
              });
              initialsMarker.openTooltip();
            })

            initialsMarker.on('mouseout', function() {
              initialsMarker.closeTooltip();
            })

            textLayers.push({
              layer: initialsMarker,
              baseFontSize: baseFontSize,
              className: 'attendee-initials',
              originalHtml: initials,
              attendeeData: attendeeData,
              effectiveRadius: Math.min(effectiveWidth, effectiveHeight) / 2,
              tableData: tableData // Pass tableData to the text layer
            })
          }
          break

        case 'circle':
          // Get chair border color based on check-in status using global status colors
          let chairStrokeColor = statusColors.none // Default for null/undefined
          let chairStrokeWidth = 2
          let chairFillColor = tableData?.colors?.chair || fill || 'transparent'
          let chairFillOpacity = 0.3
          
          // Set border colors based on check-in status using global status colors
          if (attendeeData?.assignedTicket?.checkedIn === true) {
            chairStrokeColor = statusColors.checkedin
            chairStrokeWidth = 3
            chairFillOpacity = 0.2
          } else if (attendeeData?.assignedTicket?.checkedIn === false) {
            chairStrokeColor = statusColors.checkedout
            chairStrokeWidth = 3
            chairFillOpacity = 0.2
          }

          leafletLayer = L.circle([absCenterY, absCenterX], {
            radius: effectiveRadius,
            color: chairStrokeColor, // Use the calculated chair stroke color
            weight: chairStrokeWidth, // Use the calculated chair stroke width
            fillColor: chairFillColor, // Use table-specific chair color
            fillOpacity: chairFillOpacity,
            pane: 'overlayPane',
            zIndexOffset: zIndexLevels.shapes
          }).addTo(map)

           

          // Add attendee initials if available
          if (attendeeData && attendeeData.name) {
            const initials = getInitials(attendeeData.name)
            const textLatLng = L.latLng(absCenterY, absCenterX)
            const baseFontSize = Math.max(effectiveRadius * 0.6, 10) // Make font size more proportional to radius
            const currentZoom = map.getZoom()
            const zoomScale = Math.pow(1.2, currentZoom)
            const initialScaledFontSize = baseFontSize * zoomScale

            // Use table-specific text color for attendee initials
            const textColor = tableData?.colors?.text || '#000000'

            // Make the circle size more proportional to the chair
            const circleSize = Math.max(effectiveRadius * 1.6, 24) // Minimum 24px, scales with chair

            const divIcon = L.divIcon({
              className: 'leaflet-div-icon',
              html: `<div style="
                width: ${circleSize}px;
                height: ${circleSize}px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: ${initialScaledFontSize}px;
                font-weight: 600;
                color: ${textColor};
                cursor: pointer;
                transition: all 0.2s ease;
                pointer-events: auto;
              ">${initials}</div>`,
              iconSize: [circleSize, circleSize],
              iconAnchor: [circleSize / 2, circleSize / 2]
            })
            
            const initialsMarker = L.marker(textLatLng, { 
              icon: divIcon,
              zIndexOffset: zIndexLevels.attendees
            }).addTo(map)

            // Add tooltip to both the chair shape and initials marker
            const tooltipContent = `
              <div style="font-weight: 600; margin-bottom: 4px;">${attendeeData.name}</div>
              <div style="color: #4b5563; margin-bottom: 2px;">${attendeeData.company || ''}</div>
              <div style="margin-top: 8px; font-size: 13px;">
                <div>Ticket: ${attendeeData.assignedTicket?.name || 'N/A'}</div>
                <div>Status: ${attendeeData.assignedTicket?.checkedIn ? 'Checked In' : 'Not Checked In'}</div>
                ${attendeeData.mealType ? `<div>Meal: ${attendeeData.mealType}</div>` : ''}
              </div>
            `
            
            // Bind tooltip to both the chair shape and initials marker
            leafletLayer.bindTooltip(tooltipContent, {
              permanent: false,
              direction: 'top',
              className: 'attendee-tooltip',
              offset: [0, -10],
              opacity: 1
            })

            initialsMarker.bindTooltip(tooltipContent, {
              permanent: false,
              direction: 'top',
              className: 'attendee-tooltip',
              offset: [0, -10],
              opacity: 1
            })

            // Add hover effects to both the chair shape and initials marker
            leafletLayer.on('mouseover', function() {
              // Close any open tooltips first
              map.eachLayer((layer: any) => {
                if (layer.isTooltipOpen && layer.isTooltipOpen()) {
                  layer.closeTooltip();
                }
              });
              leafletLayer.openTooltip();
            })

            leafletLayer.on('mouseout', function() {
              leafletLayer.closeTooltip();
            })

            initialsMarker.on('mouseover', function() {
              // Close any open tooltips first
              map.eachLayer((layer: any) => {
                if (layer.isTooltipOpen && layer.isTooltipOpen()) {
                  layer.closeTooltip();
                }
              });
              initialsMarker.openTooltip();
            })

            initialsMarker.on('mouseout', function() {
              initialsMarker.closeTooltip();
            })

            textLayers.push({
              layer: initialsMarker,
              baseFontSize: baseFontSize,
              className: 'attendee-initials',
              originalHtml: initials,
              attendeeData: attendeeData,
              effectiveRadius: effectiveRadius,
              tableData: tableData // Pass tableData to the text layer
            })
          }
          break

        case 'text':
        case 'i-text':
        case 'textboxAdvanced':
          // Skip text rendering if this is part of a table group
          // This prevents duplicate text rendering
          if (text && !isChair && !isTable) {
            // Handle standalone text objects including textboxAdvanced
            const textLatLng = L.latLng(objectCenterY, objectCenterX)
            const baseFontSize = fontSize || 12
            const currentZoom = map.getZoom()
            const zoomScale = Math.pow(1.2, currentZoom)
            const initialScaledFontSize = baseFontSize * zoomScale

            // For textboxAdvanced, handle backgroundColor separately
            if (type === 'textboxAdvanced') {
              // Handle textboxAdvanced with combined background and text
              // Compute canvas-space anchor from Fabric left/top, parent offsets and originX/Y
              const rawLeft = (fabricObject.left || 0) * parentScaleX
              const rawTop = (fabricObject.top || 0) * parentScaleY
              const canvasBaseX = parentOffsetX + rawLeft
              const canvasBaseY = parentOffsetY + rawTop

              let anchorCanvasX = canvasBaseX
              let anchorCanvasY = canvasBaseY

              if (fabricObject.originX === 'center') {
                // no change
              } else if (fabricObject.originX === 'right') {
                anchorCanvasX -= (effectiveWidth / 2)
              } else { // 'left' or default
                anchorCanvasX += (effectiveWidth / 2)
              }

              if (fabricObject.originY === 'center') {
                // no change
              } else if (fabricObject.originY === 'bottom') {
                anchorCanvasY -= (effectiveHeight / 2)
              } else { // 'top' or default
                anchorCanvasY += (effectiveHeight / 2)
              }

              // Convert canvas coords (top-left origin) to Leaflet CRS.Simple [lat,lng]
              const textLatLng = L.latLng(canvasHeight - anchorCanvasY, anchorCanvasX)

              const currentZoom = map.getZoom()
              const zoomScale = Math.pow(1.2, currentZoom)
              const scaledFontSize = (fabricObject.fontSize || 12) * zoomScale
              
              // Create combined background and text HTML with optional rotation
              const bgColor = fabricObject.backgroundColor || 'transparent'
              const textColor = fabricObject.fill || '#000000'
              const opacity = fabricObject.opacity !== undefined ? fabricObject.opacity : 1
              const rotation = fabricObject.angle || 0
              const rotationStyle = rotation !== 0 ? `transform: rotate(${rotation}deg);` : ''
              
              const combinedHtml = `
                <div style="
                  width: ${effectiveWidth}px;
                  height: ${effectiveHeight}px;
                  background-color: ${bgColor};
                  opacity: ${opacity};
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  text-align: ${fabricObject.textAlign || 'center'};
                  font-size: ${scaledFontSize}px;
                  font-weight: ${fabricObject.fontWeight || 'normal'};
                  color: ${textColor};
                  font-family: ${fabricObject.fontFamily || 'Arial'};
                  line-height: ${fabricObject.lineHeight || 1.16};
                  font-style: ${fabricObject.fontStyle || 'normal'};
                  text-decoration: ${[
                    fabricObject.underline ? 'underline' : '',
                    fabricObject.overline ? 'overline' : '',
                    fabricObject.linethrough ? 'line-through' : ''
                  ].filter(Boolean).join(' ') || 'none'};
                  border-radius: 2px;
                  overflow: hidden;
                  white-space: pre-wrap;
                  word-wrap: break-word;
                  padding: 2px;
                  ${rotationStyle}
                  transform-origin: center center;
                ">${fabricObject.text || ''}</div>
              `
              
              const divIcon = L.divIcon({
                html: combinedHtml,
                className: 'textbox-advanced',
                iconSize: [effectiveWidth, effectiveHeight],
                iconAnchor: [effectiveWidth / 2, effectiveHeight / 2]
              })
              
              leafletLayer = L.marker(textLatLng, { 
                icon: divIcon,
                zIndexOffset: zIndexLevels.textboxAdvanced
              }).addTo(map)

              // Add tooltip binding
              leafletLayer.bindTooltip(fabricObject.text || '', {
                permanent: false,
                direction: 'top',
                className: 'attendee-tooltip',
                offset: [0, -10],
                opacity: 1
              })

              leafletLayer.on('mouseover', function() {
                leafletLayer.openTooltip()
              })

              leafletLayer.on('mouseout', function() {
                leafletLayer.closeTooltip()
              })
              
              textLayers.push({
                layer: leafletLayer,
                baseFontSize: fabricObject.fontSize || 12,
                className: 'textbox-advanced',
                originalHtml: fabricObject.text || '',
                width: effectiveWidth,
                height: effectiveHeight,
                textAlign: fabricObject.textAlign,
                lineHeight: fabricObject.lineHeight,
                fontStyle: fabricObject.fontStyle,
                underline: fabricObject.underline,
                overline: fabricObject.overline,
                linethrough: fabricObject.linethrough,
                backgroundColor: fabricObject.backgroundColor,
                opacity: fabricObject.opacity,
                fill: fabricObject.fill
              })
            }
          }
          break

        case 'image':
          if (src) {
            const imageBounds = L.latLngBounds(
              [topLeftY, topLeftX],
              [topLeftY + effectiveHeight, topLeftX + effectiveWidth]
            )
            leafletLayer = L.imageOverlay(src, imageBounds, {
              opacity: 1,
              interactive: false
            }).addTo(map)
          }
          break

        case 'path':
          if (fabricObject.path && Array.isArray(fabricObject.path)) {
            // Convert Fabric.js path to Leaflet polygon coordinates
            const pathCommands = fabricObject.path
            const coordinates: [number, number][] = []
            let currentX = 0
            let currentY = 0
            
            // Calculate the actual position of the path object
            // For paths, we need to use the object's left/top position directly
            const pathLeft = fabricObject.left || 0
            const pathTop = fabricObject.top || 0
            
            // Account for origin offset (originX and originY)
            const originOffsetX = fabricObject.originX === 'center' ? (fabricObject.width || 0) * scaleX * parentScaleX / 2 : 0
            const originOffsetY = fabricObject.originY === 'center' ? (fabricObject.height || 0) * scaleY * parentScaleY / 2 : 0
            
            // Base position in canvas space including parent offsets
            const baseXCanvas = parentOffsetX + (pathLeft * parentScaleX) - originOffsetX
            const baseYCanvas = parentOffsetY + (pathTop * parentScaleY) - originOffsetY
            
            // Helper function to evaluate cubic Bézier curve at parameter t (0 to 1)
            const evaluateCubicBezier = (
              start: [number, number], 
              cp1: [number, number], 
              cp2: [number, number], 
              end: [number, number], 
              t: number
            ): [number, number] => {
              const mt = 1 - t;
              const mt2 = mt * mt;
              const mt3 = mt2 * mt;
              const t2 = t * t;
              const t3 = t2 * t;
              
              const x = mt3 * start[0] + 3 * mt2 * t * cp1[0] + 3 * mt * t2 * cp2[0] + t3 * end[0];
              const y = mt3 * start[1] + 3 * mt2 * t * cp1[1] + 3 * mt * t2 * cp2[1] + t3 * end[1];
              
              return [x, y];
            }
            
            // Transform a point from Fabric.js path space to Leaflet CRS.Simple [lat,lng]
            const transformPoint = (x: number, y: number): [number, number] => {
              // Scale local path coords
              const scaledX = x * scaleX * parentScaleX
              const scaledY = y * scaleY * parentScaleY
              // Convert to absolute canvas coords (top-left origin)
              const canvasX = baseXCanvas + scaledX
              const canvasY = baseYCanvas + scaledY
              // Convert to Leaflet coords (y up)
              const leafletLng = canvasX
              const leafletLat = canvasHeight - canvasY
              return [leafletLat, leafletLng]
            }
            
            pathCommands.forEach((command: any[]) => {
              const type = command[0]
              
              switch (type) {
                case 'M': // Move to
                  currentX = command[1]
                  currentY = command[2]
                  coordinates.push(transformPoint(currentX, currentY))
                  break
                  
                case 'L': // Line to
                  currentX = command[1]
                  currentY = command[2]
                  coordinates.push(transformPoint(currentX, currentY))
                  break
                  
                case 'C': // Cubic Bézier curve
                  const cp1X = command[1]
                  const cp1Y = command[2]
                  const cp2X = command[3]
                  const cp2Y = command[4]
                  const endX = command[5]
                  const endY = command[6]
                  
                  // Approximate the curve with multiple line segments
                  const segments = 200
                  for (let i = 1; i <= segments; i++) {
                    const t = i / segments;
                    const [bezierX, bezierY] = evaluateCubicBezier(
                      [currentX, currentY],
                      [cp1X, cp1Y], 
                      [cp2X, cp2Y], 
                      [endX, endY], 
                      t
                    );
                    
                    coordinates.push(transformPoint(bezierX, bezierY))
                  }
                  
                  // Update current position to end of curve
                  currentX = endX
                  currentY = endY
                  break
                  
                case 'Q': // Quadratic Bézier curve
                  const qcp1X = command[1]
                  const qcp1Y = command[2]
                  const qendX = command[3]
                  const qendY = command[4]
                  
                  // Convert quadratic to cubic Bézier for consistency
                  const qSegments = 100
                  for (let i = 1; i <= qSegments; i++) {
                    const t = i / qSegments;
                    const mt = 1 - t;
                    
                    const qx = mt * mt * currentX + 2 * mt * t * qcp1X + t * t * qendX;
                    const qy = mt * mt * currentY + 2 * mt * t * qcp1Y + t * t * qendY;
                    
                    coordinates.push(transformPoint(qx, qy))
                  }
                  
                  currentX = qendX
                  currentY = qendY
                  break
                  
                case 'Z': // Close path
                  // Close the path - Leaflet will automatically close it
                  break
                  
                default:
                  console.warn(`Unsupported path command: ${type}`)
              }
            })
            
            if (coordinates.length >= 3) {
              // Check if this is a chair and apply status colors
              let pathStrokeColor = stroke || '#000000'
              let pathStrokeWidth = strokeWidth || 1
              let pathFillColor = fill || '#cccccc'
              let pathFillOpacity = fill === 'transparent' ? 0 : 0.8
              
              if (isChair && attendeeData) {
                // Use status colors for chair borders
                pathStrokeColor = statusColors.none // Default for null/undefined
                pathStrokeWidth = 2
                pathFillColor = tableData?.colors?.chair || fill || 'transparent'
                pathFillOpacity = 0.3
                
                if (attendeeData?.assignedTicket?.checkedIn === true) {
                  pathStrokeColor = statusColors.checkedin
                  pathStrokeWidth = 3
                  pathFillOpacity = 0.2
                } else if (attendeeData?.assignedTicket?.checkedIn === false) {
                  pathStrokeColor = statusColors.checkedout
                  pathStrokeWidth = 3
                  pathFillOpacity = 0.2
                }
              }
              
              leafletLayer = L.polygon(coordinates, {
                color: pathStrokeColor,
                weight: pathStrokeWidth,
                fillColor: pathFillColor,
                fillOpacity: pathFillOpacity,
                pane: 'overlayPane',
                zIndexOffset: zIndexLevels.shapes
              }).addTo(map)
              
              // Add attendee initials if available for path chairs
              if (isChair && attendeeData && attendeeData.name) {
                const initials = getInitials(attendeeData.name)
                
                // Calculate center point of the polygon for text placement
                const centerX = coordinates.reduce((sum, coord) => sum + coord[1], 0) / coordinates.length
                const centerY = coordinates.reduce((sum, coord) => sum + coord[0], 0) / coordinates.length
                const textLatLng = L.latLng(centerY, centerX)
                
                const baseFontSize = Math.max(Math.min(effectiveWidth, effectiveHeight) * 0.3, 10)
                const currentZoom = map.getZoom()
                const zoomScale = Math.pow(1.2, currentZoom)
                const initialScaledFontSize = baseFontSize * zoomScale
          
                // Use table-specific text color for attendee initials
                const textColor = tableData?.colors?.text || '#000000'
          
                // Make the circle size proportional to the path size
                const circleSize = Math.max(Math.min(effectiveWidth, effectiveHeight) * 0.8, 24)
          
                const divIcon = L.divIcon({
                  className: 'leaflet-div-icon',
                  html: `<div style="
                    width: ${circleSize}px;
                    height: ${circleSize}px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: ${initialScaledFontSize}px;
                    font-weight: 600;
                    color: ${textColor};
                    cursor: pointer;
                    transition: all 0.2s ease;
                    pointer-events: auto;
                  ">${initials}</div>`,
                  iconSize: [circleSize, circleSize],
                  iconAnchor: [circleSize / 2, circleSize / 2]
                })
                
                const initialsMarker = L.marker(textLatLng, { 
                  icon: divIcon,
                  zIndexOffset: zIndexLevels.attendees
                }).addTo(map)
          
                // Add tooltip to both the path shape and initials marker
                const tooltipContent = `
                  <div style="font-weight: 600; margin-bottom: 4px;">${attendeeData.name}</div>
                  <div style="color: #4b5563; margin-bottom: 2px;">${attendeeData.company || ''}</div>
                  <div style="margin-top: 8px; font-size: 13px;">
                    <div>Ticket: ${attendeeData.assignedTicket?.name || 'N/A'}</div>
                    <div>Status: ${attendeeData.assignedTicket?.checkedIn ? 'Checked In' : 'Not Checked In'}</div>
                    ${attendeeData.mealType ? `<div>Meal: ${attendeeData.mealType}</div>` : ''}
                  </div>
                `
                
                // Bind tooltip to both the path shape and initials marker
                leafletLayer.bindTooltip(tooltipContent, {
                  permanent: false,
                  direction: 'top',
                  className: 'attendee-tooltip',
                  offset: [0, -10],
                  opacity: 1
                })
          
                initialsMarker.bindTooltip(tooltipContent, {
                  permanent: false,
                  direction: 'top',
                  className: 'attendee-tooltip',
                  offset: [0, -10],
                  opacity: 1
                })
          
                // Add hover effects to both the path shape and initials marker
                leafletLayer.on('mouseover', function() {
                  // Close any open tooltips first
                  map.eachLayer((layer: any) => {
                    if (layer.isTooltipOpen && layer.isTooltipOpen()) {
                      layer.closeTooltip();
                    }
                  });
                  leafletLayer.openTooltip();
                })
          
                leafletLayer.on('mouseout', function() {
                  leafletLayer.closeTooltip();
                })
          
                initialsMarker.on('mouseover', function() {
                  // Close any open tooltips first
                  map.eachLayer((layer: any) => {
                    if (layer.isTooltipOpen && layer.isTooltipOpen()) {
                      layer.closeTooltip();
                    }
                  });
                  initialsMarker.openTooltip();
                })
          
                initialsMarker.on('mouseout', function() {
                  initialsMarker.closeTooltip();
                })
          
                textLayers.push({
                  layer: initialsMarker,
                  baseFontSize: baseFontSize,
                  className: 'attendee-initials',
                  originalHtml: initials,
                  attendeeData: attendeeData,
                  effectiveRadius: Math.min(effectiveWidth, effectiveHeight) / 2,
                  tableData: tableData // Pass tableData to the text layer
                })
              }
            } else {
              console.warn('Path has insufficient coordinates for polygon creation:', coordinates)
            }
          }
          break
      }
    }

    // Process all objects
    fabricJsJsonData.objects.forEach((obj: FabricObject) => processFabricObject(obj))

    // Fit map to bounds
    if (minX !== Infinity && maxX !== -Infinity && minY !== Infinity && maxY !== -Infinity) {
      const bounds = L.latLngBounds(L.latLng(minY, minX), L.latLng(maxY, maxX))
      map.fitBounds(bounds.pad(0.1))
    } else {
      map.setView([0, 0], 0)
    }

    // Store data, in Table Label order rather than the order they were drawn
    tables.sort((a, b) => compareTableLabels(String(a.tableName), String(b.tableName)))
    setAllTables(tables)
    setFilteredTables(tables)
    textLayersRef.current = textLayers

    // Apply initial zoom scaling to text layers
    const currentZoom = map.getZoom()
    const zoomFactor = 1.2
    const baseZoomLevel = 0
    
    textLayers.forEach(item => {
      const newFontSize = item.baseFontSize * Math.pow(zoomFactor, currentZoom - baseZoomLevel)
      const currentIconOptions = item.layer.getIcon().options
      
      let newHtml
      if (item.className === 'attendee-initials' && item.attendeeData) {
        const attendeeData = item.attendeeData
        const effectiveRadius = item.effectiveRadius || 16
        const tableData = item.tableData
        
        // Use table-specific text color for attendee initials
        const textColor = tableData?.colors?.text || '#000000'
        
        // Calculate proper circle size based on effective radius
        const chairSize = Math.max(effectiveRadius * 1.6, 24)
        
        newHtml = `<div style="
          width: ${chairSize}px;
          height: ${chairSize}px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: ${newFontSize}px;
          font-weight: 600;
          color: ${textColor};
          cursor: pointer;
          transition: all 0.2s ease;
          pointer-events: auto;
        ">${item.originalHtml}</div>`
      } else if (item.className === 'table-name') {
        newHtml = `<div style="
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          text-align: center;
          font-size: ${newFontSize}px;
          font-weight: 600;
          color: ${item.tableData?.colors?.text || '#334155'};
          pointer-events: none;
          text-shadow: 1px 1px 2px rgba(255,255,255,0.8);
        ">${item.originalHtml}</div>`
      } else {
        // Handle general text and textboxAdvanced
        if (item.className === 'textbox-advanced') {
          // Handle textboxAdvanced with combined background and text
          const bgColor = item.backgroundColor || 'transparent'
          const textColor = item.fill || '#000000'
          const opacity = item.opacity !== undefined ? item.opacity : 1
          const containerW = currentIconOptions.iconSize[0]
          const containerH = currentIconOptions.iconSize[1]
          
          newHtml = `
            <div style="
              width: ${containerW}px;
              height: ${containerH}px;
              background-color: ${bgColor};
              opacity: ${opacity};
              display: flex;
              align-items: center;
              justify-content: center;
              text-align: ${item.textAlign || 'center'};
              font-size: ${newFontSize}px;
              font-weight: ${item.fontWeight || 'normal'};
              color: ${textColor};
              font-family: ${item.fontFamily || 'Arial'};
              line-height: ${item.lineHeight || 1.16};
              font-style: ${item.fontStyle || 'normal'};
              text-decoration: ${[
                item.underline ? 'underline' : '',
                item.overline ? 'overline' : '',
                item.linethrough ? 'line-through' : ''
              ].filter(Boolean).join(' ') || 'none'};
              border-radius: 2px;
              overflow: visible;
              white-space: pre-wrap;
              word-wrap: break-word;
              padding: 2px;
            ">${item.originalHtml}</div>`
        } else {
          // Handle general text
          newHtml = `<div style="
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            text-align: ${item.textAlign || 'center'};
            font-size: ${newFontSize}px;
            font-weight: ${item.fontWeight || 'normal'};
            color: ${item.fill || '#000000'};
            font-family: ${item.fontFamily || 'Arial'};
            white-space: nowrap;
            text-shadow: 1px 1px 2px rgba(255,255,255,0.8);
            pointer-events: none;
            line-height: ${item.lineHeight || 1.16};
            font-style: ${item.fontStyle || 'normal'};
            text-decoration: ${[
              item.underline ? 'underline' : '',
              item.overline ? 'overline' : '',
              item.linethrough ? 'line-through' : ''
            ].filter(Boolean).join(' ') || 'none'};
          ">${item.originalHtml}</div>`
        }
      }
      
      const newDivIcon = L.divIcon({
        className: 'custom-text-icon',
        html: newHtml,
        iconSize: currentIconOptions.iconSize,
        iconAnchor: [currentIconOptions.iconSize[0]/2, currentIconOptions.iconSize[1]/2]
      })
      item.layer.setIcon(newDivIcon)
    })

    // Add zoom handler
    map.on('zoomend', () => {
      const currentZoom = map.getZoom()
      const zoomFactor = 1.2
      const baseZoomLevel = 0

      textLayersRef.current.forEach(item => {
        const newFontSize = item.baseFontSize * Math.pow(zoomFactor, currentZoom - baseZoomLevel)
        const currentIconOptions = item.layer.getIcon().options
        
        let newHtml
        if (item.className === 'attendee-initials' && item.attendeeData) {
          const attendeeData = item.attendeeData
          const effectiveRadius = item.effectiveRadius || 16
          const tableData = item.tableData
          
          // Use table-specific text color for attendee initials
          const textColor = tableData?.colors?.text || '#000000'
          
          // Calculate proper circle size based on effective radius
          const chairSize = Math.max(effectiveRadius * 1.6, 24)
          
          newHtml = `<div style="
            width: ${chairSize}px;
            height: ${chairSize}px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: ${newFontSize}px;
            font-weight: 600;
            color: ${textColor};
            cursor: pointer;
            transition: all 0.2s ease;
            pointer-events: auto;
          ">${item.originalHtml}</div>`
        } else if (item.className === 'table-name') {
          newHtml = `<div style="
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            text-align: center;
            font-size: ${newFontSize}px;
            font-weight: 600;
            color: ${item.tableData?.colors?.text || '#334155'};
            pointer-events: none;
            text-shadow: 1px 1px 2px rgba(255,255,255,0.8);
          ">${item.originalHtml}</div>`
        } else if (item.className === 'table-text') {
          newHtml = `<div style="
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            text-align: ${item.textAlign || 'left'};
            font-size: ${newFontSize}px;
            font-weight: ${item.fontWeight || 'normal'};
            color: ${item.fill || '#000000'};
            font-family: ${item.fontFamily || 'Arial'};
            white-space: nowrap;
            pointer-events: auto;
            cursor: pointer;
          ">${item.originalHtml}</div>`
        } else if (item.className === 'table-text') {
          newHtml = `<div style="
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            text-align: ${item.textAlign || 'left'};
            font-size: ${newFontSize}px;
            font-weight: ${item.fontWeight || 'normal'};
            color: ${item.fill || '#000000'};
            font-family: ${item.fontFamily || 'Arial'};
            white-space: nowrap;
            pointer-events: auto;
            cursor: pointer;
          ">${item.originalHtml}</div>`
        } else {
          // Handle general text and textboxAdvanced
          if (item.className === 'textbox-advanced') {
            // Handle textboxAdvanced with combined background and text
            const bgColor = item.backgroundColor || 'transparent'
            const textColor = item.fill || '#000000'
            const opacity = item.opacity !== undefined ? item.opacity : 1
            const containerW = currentIconOptions.iconSize[0]
            const containerH = currentIconOptions.iconSize[1]
            
            newHtml = `
              <div style="
                width: ${containerW}px;
                height: ${containerH}px;
                background-color: ${bgColor};
                opacity: ${opacity};
                display: flex;
                align-items: center;
                justify-content: center;
                text-align: ${item.textAlign || 'center'};
                font-size: ${newFontSize}px;
                font-weight: ${item.fontWeight || 'normal'};
                color: ${textColor};
                font-family: ${item.fontFamily || 'Arial'};
                line-height: ${item.lineHeight || 1.16};
                font-style: ${item.fontStyle || 'normal'};
                text-decoration: ${[
                  item.underline ? 'underline' : '',
                  item.overline ? 'overline' : '',
                  item.linethrough ? 'line-through' : ''
                ].filter(Boolean).join(' ') || 'none'};
                border-radius: 2px;
                overflow: visible;
                white-space: pre-wrap;
                word-wrap: break-word;
                padding: 2px;
              ">${item.originalHtml}</div>
            `
          } else {
            // Handle general text
            newHtml = `<div style="
              display: flex;
              align-items: center;
              justify-content: center;
              width: 100%;
              height: 100%;
              text-align: ${item.textAlign || 'center'};
              font-size: ${newFontSize}px;
              font-weight: ${item.fontWeight || 'normal'};
              color: ${item.fill || '#000000'};
              font-family: ${item.fontFamily || 'Arial'};
              white-space: nowrap;
              text-shadow: 1px 1px 2px rgba(255,255,255,0.8);
              pointer-events: none;
              line-height: ${item.lineHeight || 1.16};
              font-style: ${item.fontStyle || 'normal'};
              text-decoration: ${[
                item.underline ? 'underline' : '',
                item.overline ? 'overline' : '',
                item.linethrough ? 'line-through' : ''
              ].filter(Boolean).join(' ') || 'none'};
            ">${item.originalHtml}</div>`
          }
        }
        
        const newDivIcon = L.divIcon({
          className: 'custom-text-icon',
          html: newHtml,
          iconSize: currentIconOptions.iconSize,
          iconAnchor: [currentIconOptions.iconSize[0]/2, currentIconOptions.iconSize[1]/2]
        })
        item.layer.setIcon(newDivIcon)
      })
    })
  }

  // Sync attendees hook
  const {
    isSyncing,
    lastSyncTime,
    syncError,
    fetchAndSyncAttendees,
    abortControllerRef
  } = useSyncAttendees({
    eventId: eventId!,
    id: id!,
    tableNameFromUrl,
    mapRef,
    floorMapDesignMeta,
    showSnackbar,
    clearMapLayers,
    processFabricData
  });

  // Initialize map and load Leaflet assets
  useEffect(() => {
    if (!floorMapDesignMeta?.filteredVisualDesigns?.length) {
      if (!isLoading) setIsLoading(true);
      return
    }

    setIsLoading(false)

    const leafletCSS = document.createElement('link')
    leafletCSS.rel = 'stylesheet'
    leafletCSS.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
    document.head.appendChild(leafletCSS)

    const leafletScript = document.createElement('script')
    leafletScript.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
    document.head.appendChild(leafletScript)

    leafletScript.onload = () => {
      if (!mapRef.current) {
        initMap()
      } else {
        console.log("Map already initialized, fetching attendees for initial sync.");
        fetchAndSyncAttendees();
      }
    }

    return () => {
      console.log('Cleaning up Leaflet resources...');
      const scripts = document.querySelectorAll('script[src*="leaflet"]')
      const links = document.querySelectorAll('link[href*="leaflet"]')
      
      scripts.forEach(script => script.remove())
      links.forEach(link => link.remove())
      
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.cancel('Component unmounting');
      }
    }
  }, [floorMapDesignMeta?.filteredVisualDesigns?.length, id, eventId])

  const initMap = async () => {
    if (!mapContainerRef.current) return

    console.log('Initializing Leaflet map...');
    const map = L.map(mapContainerRef.current, {
      crs: L.CRS.Simple,
      minZoom: -2,
      maxZoom: 2,
      zoomSnap: 0.5,
      center: [0, 0],
      zoom: 0,
      attributionControl: false,
      scrollWheelZoom: true
    })

    mapRef.current = map

    const templates = floorMapDesignMeta?.filteredVisualDesigns
    if (templates?.length > 0) {
      const template = templates.find(item => item.id == id)
      if (template) {
        setBadgeName(template.name || 'Gala Layout')
        
        const templateStatusColors = template.template?.options?.tableDataGlobal?.statusColors
        if (templateStatusColors) {
          setStatusColors(prevColors => ({ ...prevColors, ...templateStatusColors }));
        }
        
        const fabricJsJsonData = template.template.pageData[0].templateData
        if (fabricJsJsonData && fabricJsJsonData.objects) {
          await fetchAndSyncAttendees();
        } else {
          console.warn('No Fabric.js template data found for the selected design.');
          showSnackbar('Design data is empty or invalid.', 'error');
        }
      } else {
        console.warn(`No template found with ID: ${id}`);
        showSnackbar(`No template found with ID: ${id}`, 'error');
      }
    } else {
      console.warn('No visual designs available for the event.');
      showSnackbar('No floor map designs found for this event.', 'error');
    }
  }

  const filterTables = () => {
    let filtered = allTables;
    
    if (isTableSpecificView && tableNameFromUrl) {
      console.log(`🎯 Table-specific view: Filtering for table "${tableNameFromUrl}"`);
      filtered = allTables.filter(table => {
        const tableName = table.tableName.toLowerCase();
        const urlTableName = tableNameFromUrl.toLowerCase();
        
        // Exact match
        if (tableName === urlTableName) return true;
        
        // Try with and without spaces
        const noSpaceTableName = tableName.replace(/\s+/g, '');
        const noSpaceUrlName = urlTableName.replace(/\s+/g, '');
        
        return noSpaceTableName === noSpaceUrlName;
      });
    } else {
      if (searchTerm.trim() !== '') {
        const searchLower = searchTerm.toLowerCase().trim();
        filtered = filtered.filter(table => {
          const tableNameMatch = table.tableName.toLowerCase().includes(searchLower);
          return tableNameMatch;
        });
      } else if (attendeeSearchTerm.trim() !== '') {
        const attendeeSearchLower = attendeeSearchTerm.toLowerCase().trim();
        filtered = filtered.filter(table => {
          return table.attendees.some(att => 
            att.name.toLowerCase().includes(attendeeSearchLower) ||
            (att.company && att.company.toLowerCase().includes(attendeeSearchLower))
          );
        });
      }

      if (ticketNameFilter !== 'all') {
        filtered = filtered.filter(table => {
          return table.attendees.some(att => 
            att.assignedTicket?.name === ticketNameFilter
          );
        });
      }

      switch (currentFilter) {
        case 'checked-in':
          filtered = filtered.filter(table => 
            table.attendees.length > 0 && 
            table.attendees.every(att => att.assignedTicket?.checkedIn === true)
          );
          break;
        case 'not-checked-in':
          filtered = filtered.filter(table => 
            table.attendees.length > 0 && 
            table.attendees.every(att => att.assignedTicket?.checkedIn === false)
          );
          break;
        case 'partially-checked-in':
          filtered = filtered.filter(table => {
            const hasCheckedIn = table.attendees.some(att => att.assignedTicket?.checkedIn === true);
            const hasNotCheckedIn = table.attendees.some(att => att.assignedTicket?.checkedIn === false);
            return hasCheckedIn && hasNotCheckedIn;
          });
          break;
        case 'empty':
          filtered = filtered.filter(table => 
            (table.capacity - table.attendees.length) > 0
          );
          break;
        case 'full':
          filtered = filtered.filter(table => 
            table.attendees.length === table.capacity
          );
          break;
      }
    }
    
    setFilteredTables(filtered);
  };

  useEffect(() => {
    filterTables();
  }, [searchTerm, attendeeSearchTerm, ticketNameFilter, currentFilter, allTables]);

  useEffect(() => {
    if (eventId) {
      localStorage.setItem("eventId", eventId)
      switchApp(AppTypes.floormap)
      dispatch(changeActivePanel("mapping"))
      dispatch(changeIsAttendeeFloorMap(true))
    }
  }, [eventId])

  const handleBackClick = () => {
    setSelectedTable(null)
    setSearchTerm('')
    setAttendeeSearchTerm('')
    setTicketNameFilter('all')
    setCurrentFilter('all')
  }

  const handleManualRefresh = () => {
    console.log('Manual refresh triggered');
    fetchAndSyncAttendees();
  };

  // The latest sync, for listeners attached once per event. A change that
  // arrives before the map is up needs nothing: the first sync covers it.
  const refreshSeatingRef = useRef<() => void>(() => undefined);
  refreshSeatingRef.current = () => {
    if (!mapRef.current || !floorMapDesignMeta?.filteredVisualDesigns?.length) return;
    fetchAndSyncAttendees();
  };

  useEffect(() => {
    if (!eventId) return;
    return attachLiveSeating({ eventId, refresh: () => refreshSeatingRef.current() });
  }, [eventId]);

  useEffect(() => {
    if (isDrawerOpen) {
      setSearchTerm('')
      setAttendeeSearchTerm('')
      setTicketNameFilter('all')
      setCurrentFilter('all')
    }
  }, [isDrawerOpen])

  // A saved room renames and recolours the view as it redraws it.
  useEffect(() => {
    if (!savedDesign) return;
    setBadgeName(savedDesign.name || 'Gala Layout');
    const savedStatusColors = savedDesign.template?.options?.tableDataGlobal?.statusColors;
    if (savedStatusColors) {
      setStatusColors(prevColors => ({ ...prevColors, ...savedStatusColors }));
    }
    // With nobody seated there is nothing stored to redraw the room around.
    if (!attendeesFromStore?.length) refreshSeatingRef.current();
  }, [savedDesign]);

  useEffect(() => {
    if (!attendeesFromStore?.length || !mapRef.current || !floorMapDesignMeta?.filteredVisualDesigns?.length) {
      return;
    }

    console.log('Redux attendees store updated, re-rendering map...');

    const template = floorMapDesignMeta.filteredVisualDesigns.find(item => item.id == id);
    if (!template || !template.template.pageData[0].templateData) {
      return;
    }

    const fabricJsJsonData = template.template.pageData[0].templateData;
    const flattenedData = attendeesFromStore.flat(3);
    let cancelled = false;

    const renderStoredAssignments = async () => {
      // The module decides which Ticket sits at which Table; the store only
      // carries the rows the assignments were read from (ADR-0001).
      const processedAttendees = await seatingForDesign(
        [{ templateData: fabricJsJsonData }],
        async () => flattenedData
      );

      // The map may have been torn down, or a newer store update started, while
      // the module was resolving.
      if (cancelled || !mapRef.current) {
        return;
      }

      let mergedData = mergeFabricDataWithAPIData(fabricJsJsonData, processedAttendees);

      if (tableNameFromUrl) {
        const normalizeTableName = (name: string) => {
          // Normalize by removing spaces and converting to lowercase
          return name.toLowerCase().replace(/\s+/g, '');
        };

        const singleTable = mergedData.objects.find((obj: any) => {
          if (!obj.tableData) return false;

          const tableNumber = obj.tableData.tableNumber;
          const tableName = obj.tableData.tableName;

          const normalizedSearch = normalizeTableName(tableNameFromUrl);
          const normalizedNumber = tableNumber ? normalizeTableName(tableNumber) : '';
          const normalizedName = tableName ? normalizeTableName(tableName) : '';

          return normalizedNumber === normalizedSearch || normalizedName === normalizedSearch;
        });

        mergedData = { objects: singleTable ? [singleTable] : [] };
      }

      clearMapLayers();
      processFabricData(mapRef.current, mergedData);

      // Force a zoom event to ensure proper text sizing after Redux sync
      mapRef.current.setZoom(mapRef.current.getZoom());
    };

    void renderStoredAssignments();

    return () => {
      cancelled = true;
    };
  }, [attendeesFromStore, mapRef.current, floorMapDesignMeta?.filteredVisualDesigns?.length, id, tableNameFromUrl, currentDesign?.template, statusColors]);

  // Seat Assignments live in the backend, not in the Design (ADR-0001), so an
  // unfetched room would read as "nobody is seated". It is covered until the
  // first fetch lands — as an overlay, not an early return, because the map
  // only initializes once the container it draws into is mounted, and a failed
  // fetch has to uncover the Refresh control rather than spin forever.
  const hasFetchedAssignments = !!lastSyncTime
  if (isLoading || !floorMapDesignMeta?.filteredVisualDesigns?.length) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
      >
        <CircularProgress size={50} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading Gala Layout...
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <style>
        {`
          #map { 
            width: ${isDrawerOpen ? 'calc(100vw - 400px)' : '100vw'};
            height: 100vh;
            transition: width 0.3s ease;
          }
          
          @media (max-width: 768px) {
            #map {
              width: 100vw !important;
            }
          }
          
          .leaflet-control-attribution {
            display: none !important;
          }
          
          .leaflet-image-layer {
            z-index: ${zIndexLevels.background} !important;
          }
          
          .leaflet-interactive {
            z-index: ${zIndexLevels.shapes} !important;
          }
          
          .table-name {
            z-index: ${zIndexLevels.tables} !important;
            position: relative !important;
          }
          
          .general-text {
            z-index: ${zIndexLevels.text} !important;
            position: relative !important;
          }
          
          .textbox-advanced {
            z-index: ${zIndexLevels.textboxAdvanced} !important;
            position: relative !important;
          }
          
          .attendee-initials {
            z-index: ${zIndexLevels.attendees} !important;
            position: relative !important;
          }
          
          .leaflet-marker-pane .leaflet-marker-icon {
            z-index: auto !important;
          }
          
          .leaflet-marker-pane .leaflet-marker-icon.table-name {
            z-index: ${zIndexLevels.tables} !important;
          }
          
          .leaflet-marker-pane .leaflet-marker-icon.general-text {
            z-index: ${zIndexLevels.text} !important;
          }
          
          .leaflet-marker-pane .leaflet-marker-icon.textbox-advanced {
            z-index: ${zIndexLevels.textboxAdvanced} !important;
          }
          
          .leaflet-marker-pane .leaflet-marker-icon.attendee-initials {
            z-index: ${zIndexLevels.attendees} !important;
          }
          
          .leaflet-tooltip {
            z-index: ${zIndexLevels.attendees + 100} !important;
          }
        `}
      </style>

      <ToggleButton
        onClick={() => {
          if (!isDrawerOpen) {
            setSearchTerm('')
            setAttendeeSearchTerm('')
            setTicketNameFilter('all')
            setCurrentFilter('all')
          }
          setIsDrawerOpen(!isDrawerOpen)
        }}
        aria-label="toggle drawer"
        size="small"
        color="primary"
      >
        {isDrawerOpen ? <CloseIcon sx={{ fontSize: 16 }} /> : <MenuIcon sx={{ fontSize: 16 }} />}
      </ToggleButton>

      <DrawerBackdrop 
        isOpen={isDrawerOpen} 
        onClick={() => setIsDrawerOpen(false)} 
      />

      <DrawerContainer isOpen={isDrawerOpen}>
        <DrawerHeader
          selectedTable={selectedTable}
          isTableSpecificView={isTableSpecificView}
          tableNameFromUrl={tableNameFromUrl}
          isSyncing={isSyncing}
          onBackClick={handleBackClick}
          onManualRefresh={handleManualRefresh}
          onClose={() => setIsDrawerOpen(false)}
        />

        {(lastSyncTime || syncError) && (
          <div style={{ 
            padding: '8px 20px', 
            backgroundColor: syncError ? '#fef2f2' : '#f0fdf4',
            borderBottom: '1px solid #e5e7eb',
            fontSize: '0.875rem'
          }}>
            {syncError ? (
              <div style={{ color: '#dc2626' }}>
                ⚠️ Sync error: {syncError}
              </div>
            ) : lastSyncTime ? (
              <div style={{ color: '#16a34a' }}>
                ✅ Last synced: {lastSyncTime.toLocaleTimeString()}
              </div>
            ) : null}
          </div>
        )}

        {!selectedTable && !isTableSpecificView && (
          <SearchFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            attendeeSearchTerm={attendeeSearchTerm}
            setAttendeeSearchTerm={setAttendeeSearchTerm}
            ticketNameFilter={ticketNameFilter}
            setTicketNameFilter={setTicketNameFilter}
            currentFilter={currentFilter}
            setCurrentFilter={setCurrentFilter}
            allTables={allTables}
          />
        )}

        <div style={{ padding: '20px', overflowY: 'auto', flexGrow: 1 }}>
          {isTableSpecificView && tableNameFromUrl && !selectedTable && (
            <div style={{ 
              padding: '12px 16px', 
              backgroundColor: '#eff6ff', 
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '0.875rem',
              color: '#1e40af'
            }}>
              🎯 <strong>Table-specific view:</strong> Showing data for Table {tableNameFromUrl}
              {filteredTables.length === 0 && (
                <div style={{ marginTop: '8px', color: '#dc2626' }}>
                  ⚠️ Table "{tableNameFromUrl}" not found in the current layout
                </div>
              )}
            </div>
          )}

          {isTableSpecificView && tableNameFromUrl && filteredTables.length === 0 && (
            <Snackbar
              open={true}
              autoHideDuration={6000}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              sx={{ zIndex: 2000 }}
            >
              <Alert
                severity="warning"
                variant="filled"
                sx={{ width: '100%' }}
              >
                ⚠️ Table "{tableNameFromUrl}" not found in the current layout
              </Alert>
            </Snackbar>
          )}
          
          {selectedTable ? (
            <TableDetails selectedTable={selectedTable} />
          ) : (
            <TableList 
              filteredTables={filteredTables} 
              onTableClick={setSelectedTable} 
            />
          )}
        </div>
      </DrawerContainer>

      <div id="map" ref={mapContainerRef}></div>

      {!hasFetchedAssignments && !syncError && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1500,
            backgroundColor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CircularProgress size={50} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading Seat Assignments...
          </Typography>
        </Box>
      )}

      {badgeName && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            padding: '8px 16px',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 1000,
            maxWidth: '80%',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap'
          }}
        >
          <Typography variant="h6" component="h1" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
            {badgeName}
          </Typography>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ zIndex: 2000 }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default AttendeeGalaLeafRefactored 